/* Fundle Data Bridge page — Orchid.
 *
 * Operates the upstream pull from `https://databridge.fundlezone.com/api/data-bridge`.
 * Shows config, live sync status (counts), and provides "Sync Now" + "Reset" controls.
 */
import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, SectionHeading } from "./_shared";
import { fmtDateTime } from "@/lib/format";
import {
  RefreshCw, Database, CheckCircle2, AlertCircle, Loader2, Clock,
  KeyRound, Plug, Activity, RotateCcw, Eye, EyeOff,
} from "lucide-react";

function StatCard({ label, value, accent, testid }) {
  return (
    <div className="bg-white border-l-4 px-5 py-4" style={{ borderLeftColor: accent || "#1F5135" }}>
      <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-500 mb-1">{label}</div>
      <div className="text-3xl font-display kazo-text-black" data-testid={testid}>{value}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    ok: { bg: "#ECFDF5", color: "#047857", border: "#A7F3D0", label: "Healthy", Icon: CheckCircle2 },
    error: { bg: "#FEF2F2", color: "#B91C1C", border: "#FECACA", label: "Errored", Icon: AlertCircle },
    running: { bg: "#DBEAFE", color: "#1E40AF", border: "#BFDBFE", label: "Syncing", Icon: Loader2 },
  };
  const s = map[status] || { bg: "#F3F4F6", color: "#374151", border: "#E5E7EB", label: "Idle", Icon: Clock };
  const Icon = s.Icon;
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium border" style={{ background: s.bg, color: s.color, borderColor: s.border }} data-testid="bridge-status-pill">
      <Icon className={`w-3.5 h-3.5 ${status === "running" ? "animate-spin" : ""}`} />
      {s.label}
    </span>
  );
}

function fmtInt(n) {
  if (n == null) return "—";
  return Number(n).toLocaleString("en-IN");
}

export default function DataBridgePage() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showKey, setShowKey] = useState(false);
  const [edit, setEdit] = useState({});
  const [savingCfg, setSavingCfg] = useState(false);
  const [dwState, setDwState] = useState(null);
  const [dwFrom, setDwFrom] = useState("2016-01-01");
  const [dwTo, setDwTo] = useState("2026-07-02");
  const [csvState, setCsvState] = useState(null);
  const [csvUrl, setCsvUrl] = useState("");
  const pollRef = useRef(null);

  async function load() {
    try {
      const r = await api.get("/data-bridge/status");
      setStatus(r.data);
      try {
        const dw = await api.get("/data-bridge/date-window-backfill/status");
        setDwState(dw.data);
      } catch (_) { /* endpoint may not exist yet on old deploys */ }
      try {
        const cv = await api.get("/data-bridge/csv-import/status");
        setCsvState(cv.data);
      } catch (_) { /* endpoint may not exist yet on old deploys */ }
    } catch (e) {
      toast.error(`Failed to load Data Bridge status: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 5000);
    return () => clearInterval(pollRef.current);
  }, []);

  async function testConnection() {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.post("/data-bridge/test-connection");
      setTestResult(r.data);
      if (r.data.ok) toast.success(`Connection OK · brand ${r.data.brand_id} · ${fmtInt(r.data.total_customers)} guests upstream`);
      else toast.error(`Connection failed: ${r.data.error || "unknown"}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      setTestResult({ ok: false, error: msg });
      toast.error(`Connection failed: ${msg}`);
    } finally {
      setTesting(false);
    }
  }

  async function syncNow(full = false) {
    setSyncing(true);
    try {
      await api.post("/data-bridge/sync-now", { full });
      toast.success(full ? "Full re-sync queued" : "Sync queued — pulling next batch from Fundle");
      setTimeout(load, 1500);
    } catch (e) {
      toast.error(`Sync failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSyncing(false);
    }
  }

  async function resetCursors() {
    if (!window.confirm("Reset all sync cursors? Next sync will start from scratch (re-ingests are idempotent).")) return;
    try {
      await api.post("/data-bridge/reset-cursors");
      toast.success("Cursors reset");
      load();
    } catch (e) {
      toast.error(`Reset failed: ${e?.response?.data?.detail || e.message}`);
    }
  }

  async function skipToIncremental() {
    const ok = window.confirm(
      "SKIP TO INCREMENTAL MODE\n\n" +
      "This marks the initial backfill as COMPLETE and uses the latest bill_date " +
      "already in our DB as the cursor. The next tick will use Fundle's fast date-cursor " +
      "path (with 7-day lookback) instead of re-scanning millions of already-ingested rows.\n\n" +
      "Use this when 'Full Re-sync' or a stuck deep-OFFSET has the backfill walking through " +
      "existing data very slowly.\n\n" +
      "Click OK to proceed."
    );
    if (!ok) return;
    try {
      const r = await api.post("/data-bridge/skip-to-incremental");
      toast.success(
        `Switched to incremental mode · cursor = ${r.data?.cursor_transactions || "?"}`,
        { duration: 6000 }
      );
      load();
    } catch (e) {
      toast.error(`Skip failed: ${e?.response?.data?.detail || e.message}`);
    }
  }

  async function hardReset() {
    const ok = window.confirm(
      "⚠ HARD RESET\n\n" +
      "This will:\n" +
      "  1. DELETE all bridge-sourced folios, guests, ledger rows and properties\n" +
      "  2. Reset cursors to zero\n" +
      "  3. Begin re-syncing all ~2M Fundle records from scratch (~60-90 min)\n\n" +
      "Use this only to recover from corrupted local data (e.g. the pre-fix cursor-race bug).\n\n" +
      "Click OK to proceed, Cancel to abort."
    );
    if (!ok) {
      toast.info("Cancelled — nothing deleted.");
      return;
    }
    try {
      const r = await api.post("/data-bridge/hard-reset");
      toast.success(
        r.data?.message || "Hard reset queued · deletion running in background · sync will restart automatically",
        { duration: 8000 }
      );
      load();
    } catch (e) {
      toast.error(`Hard reset failed: ${e?.response?.data?.detail || e.message}`);
    }
  }

  async function saveConfig() {
    setSavingCfg(true);
    try {
      const payload = {};
      ["base_url", "api_key", "brand_id", "auth_header", "sync_interval_min", "page_size"].forEach((k) => {
        if (edit[k] !== undefined && edit[k] !== "") payload[k] = k === "brand_id" || k === "sync_interval_min" || k === "page_size" ? Number(edit[k]) : edit[k];
      });
      if (Object.keys(payload).length === 0) {
        toast.info("Nothing to save");
        setSavingCfg(false);
        return;
      }
      await api.put("/data-bridge/config", payload);
      toast.success("Config saved");
      setEdit({});
      load();
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSavingCfg(false);
    }
  }

  async function toggleSyncEnabled() {
    try {
      await api.put("/data-bridge/config", { sync_enabled: !status.config.sync_enabled });
      toast.success(`Auto-sync ${status.config.sync_enabled ? "paused" : "resumed"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  }

  async function startDateWindowBackfill() {
    const ok = window.confirm(
      "HISTORICAL DATE-WINDOW BACKFILL\n\n" +
      `Range: ${dwFrom}  →  ${dwTo}\n\n` +
      "This will walk Fundle month-by-month (bypassing the deep-OFFSET dead zone that " +
      "left pre-2020 data unfetched) and populate our local Mongo with the FULL history. " +
      "Runs in the background — safe to close this page and return.\n\n" +
      "Idempotent: already-ingested folios are no-ops. Estimated ~10-30 min for the " +
      "full 2016 → today range.\n\n" +
      "Click OK to start."
    );
    if (!ok) return;
    try {
      const r = await api.post("/data-bridge/date-window-backfill/start", { from_date: dwFrom, to_date: dwTo });
      toast.success(`Started · ${r.data?.months} months queued`, { duration: 5000 });
      load();
    } catch (e) {
      toast.error(`Start failed: ${e?.response?.data?.detail || e.message}`);
    }
  }

  async function stopDateWindowBackfill() {
    try {
      await api.post("/data-bridge/date-window-backfill/stop");
      toast.info("Stop signal sent — current month will finish then loop halts.");
      load();
    } catch (e) {
      toast.error(`Stop failed: ${e?.response?.data?.detail || e.message}`);
    }
  }

  async function startCsvImport() {
    if (!csvUrl || !csvUrl.startsWith("http")) {
      toast.error("Paste the zip artifact URL first.");
      return;
    }
    const ok = window.confirm(
      "ONE-TIME HISTORICAL CSV IMPORT\n\n" +
      "This will:\n" +
      "  1. Pause the Data Bridge auto-sync\n" +
      "  2. WIPE all existing folios, guests, ledger, items, stores\n" +
      "  3. Download the CSV zip from your URL\n" +
      "  4. Ingest all 5 CSVs (stores, guests, points, folios, itemwise)\n" +
      "  5. Populate dashboards & reports from local Mongo\n\n" +
      "Duration: ~10-15 min on preview, ~15-25 min on Atlas.\n" +
      "This is IRREVERSIBLE — you cannot un-wipe.\n\n" +
      "Click OK to proceed."
    );
    if (!ok) return;
    try {
      const r = await api.post("/data-bridge/csv-import/start", { artifact_url: csvUrl, wipe_first: true });
      toast.success("CSV import queued · watch the progress panel below.", { duration: 5000 });
      load();
    } catch (e) {
      toast.error(`Start failed: ${e?.response?.data?.detail || e.message}`);
    }
  }

  if (loading) return <div className="p-8 text-neutral-500">Loading Data Bridge…</div>;
  if (!status) return <div className="p-8 text-red-600">Failed to load.</div>;

  const cfg = status.config || {};
  const counts = status.local_counts || {};
  const summary = cfg.last_sync_summary || {};
  const txStats = summary.transactions || {};
  const liveStatus = cfg.running ? "running" : (cfg.last_sync_status || "idle");

  return (
    <div data-testid="data-bridge-page" className="space-y-8">
      <PageHeader
        title="Fundle Data Bridge"
        subtitle="Live sync of guests, folios &amp; pre-computed loyalty points from the upstream Fundle system. Points & tiers are read-only mirrors — earn / burn happens at Fundle."
        actions={
          <div className="flex items-center gap-2">
            <StatusPill status={liveStatus} />
            <button
              onClick={() => syncNow(false)}
              disabled={syncing || cfg.running}
              className="k-btn kazo-bg-burgundy text-white uppercase tracking-widest text-[11px] font-semibold disabled:opacity-50"
              data-testid="bridge-sync-now-btn"
            >
              <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} /> Sync Now
            </button>
            <button
              onClick={testConnection}
              disabled={testing}
              className="k-btn border-2 border-[var(--kazo-black)] text-[var(--kazo-black)] uppercase tracking-widest text-[11px] font-semibold disabled:opacity-50"
              data-testid="bridge-test-conn-btn"
            >
              <Plug className={`w-4 h-4 ${testing ? "animate-spin" : ""}`} /> Test Connection
            </button>
          </div>
        }
      />

      {/* Live counts in our local DB */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Folios (transactions)" value={fmtInt(counts.transactions)} accent="#1F5135" testid="bridge-count-transactions" />
        <StatCard label="Guests (customers)" value={fmtInt(counts.customers)} accent="#0F3D2E" testid="bridge-count-customers" />
        <StatCard label="Properties (hotels)" value={fmtInt(counts.stores)} accent="#C9A86E" testid="bridge-count-stores" />
        <StatCard label="Points ledger rows" value={fmtInt(counts.points_ledger)} accent="#8B5A2B" testid="bridge-count-ledger" />
      </div>

      {/* Sync status */}
      <section className="bg-white border border-black/10">
        <SectionHeading title="Sync Status" />
        <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-1">Last sync</div>
            <div className="font-medium kazo-text-black" data-testid="bridge-last-sync-at">{cfg.last_sync_at ? fmtDateTime(cfg.last_sync_at) : "Never"}</div>
            {cfg.last_error && <div className="mt-1 text-[12px] text-red-700" data-testid="bridge-last-error">{cfg.last_error}</div>}
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-1">Initial backfill</div>
            <div className="font-medium kazo-text-black" data-testid="bridge-initial-done">
              {cfg.initial_sync_done ? <><CheckCircle2 className="inline w-4 h-4 text-emerald-700" /> Complete</> : <>In progress · {fmtInt(cfg.cursor_offset_initial)} rows scanned</>}
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-1">Incremental cursor</div>
            <div className="font-medium kazo-text-black break-all" data-testid="bridge-cursor-txn">{cfg.cursor_transactions || "—"}</div>
          </div>
          {summary.in_progress && (
            <div className="md:col-span-3 -mt-2">
              <div className="text-[12px] text-emerald-700 flex items-center gap-2" data-testid="bridge-in-progress">
                <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                Sync in progress · started {summary.started_at ? fmtDateTime(summary.started_at) : "—"}
              </div>
            </div>
          )}
          {txStats && txStats.pulled !== undefined && !summary.in_progress && (
            <>
              <div>
                <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-1">Last tick · pulled</div>
                <div className="font-medium" data-testid="bridge-last-pulled">{fmtInt(txStats.pulled)} rows / {txStats.pages || 0} pages</div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-1">Last tick · upserted</div>
                <div className="font-medium">{fmtInt(txStats.upserted_txn)} folios · {fmtInt(txStats.upserted_cust)} guests · {fmtInt(txStats.ledger_rows)} ledger</div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-1">Errors</div>
                <div className={`font-medium ${txStats.errors ? "text-red-700" : "kazo-text-black"}`}>{fmtInt(txStats.errors)}</div>
              </div>
            </>
          )}
        </div>
        <div className="px-5 pb-5 flex flex-wrap gap-2 items-center">
          <button onClick={toggleSyncEnabled} className="text-xs uppercase tracking-widest border border-black/30 px-3 py-2 hover:bg-black hover:text-white transition" data-testid="bridge-toggle-enabled">
            {cfg.sync_enabled ? "Pause auto-sync" : "Resume auto-sync"}
          </button>
          <button onClick={() => syncNow(true)} disabled={cfg.running} className="text-xs uppercase tracking-widest border border-amber-700 text-amber-800 px-3 py-2 hover:bg-amber-50 transition disabled:opacity-50" data-testid="bridge-full-resync-btn">
            <RotateCcw className="inline w-3.5 h-3.5 mr-1" /> Full Re-sync
          </button>
          {!cfg.initial_sync_done && (
            <button onClick={skipToIncremental} className="text-xs uppercase tracking-widest border border-emerald-700 text-emerald-800 px-3 py-2 hover:bg-emerald-50 transition" data-testid="bridge-skip-to-incremental-btn" title="Mark backfill complete and switch to fast date-cursor mode using max bill_date in our DB">
              ⚡ Skip to Incremental
            </button>
          )}
          <button onClick={resetCursors} className="text-xs uppercase tracking-widest border border-red-700 text-red-800 px-3 py-2 hover:bg-red-50 transition" data-testid="bridge-reset-cursors-btn">
            Reset Cursors
          </button>
          <button onClick={hardReset} className="text-xs uppercase tracking-widest border-2 border-red-700 bg-red-700 text-white px-3 py-2 hover:bg-red-800 transition" data-testid="bridge-hard-reset-btn">
            HARD RESET (Re-sync from scratch)
          </button>
          {testResult && (
            <div className={`text-[12px] px-3 py-2 ${testResult.ok ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-800"}`} data-testid="bridge-test-result">
              {testResult.ok ? `OK · brand ${testResult.brand_id} · ${fmtInt(testResult.total_customers)} guests upstream` : `Failed: ${testResult.error}`}
            </div>
          )}
        </div>
      </section>

      {/* One-time historical CSV import */}
      <section className="bg-white border-2 border-purple-700/50" data-testid="csv-import-section">
        <SectionHeading title="One-Time Historical CSV Import" />
        <div className="p-5 space-y-4">
          <div className="text-[13px] text-neutral-700 leading-relaxed">
            Wipes ALL existing bridge data and re-populates from a Fundle-provided
            CSV zip (5 files: stores, guests, points balance, folios, itemwise).
            Use this once to load historical data before switching to live sync.
            Ingests ~4.4M rows in ~10-25 min. <b>Irreversible.</b>
          </div>
          {csvState && (csvState.running || csvState.phase === "done" || csvState.phase === "failed") && (
            <div className="bg-purple-50 border border-purple-200 p-3 text-sm space-y-1" data-testid="csv-import-progress">
              <div className="flex items-center gap-2 text-purple-800 font-medium">
                {csvState.running && <RefreshCw className="w-4 h-4 animate-spin" />}
                {csvState.phase === "done" ? "✅ Import complete" : csvState.phase === "failed" ? "❌ Failed" : `Phase: ${csvState.phase}`}
              </div>
              <div className="text-[12px] text-neutral-700 grid grid-cols-2 md:grid-cols-5 gap-2 mt-2">
                <div>Stores: <b>{fmtInt(csvState.stores_upserted)}</b></div>
                <div>Guests: <b>{fmtInt(csvState.customers_done)}</b></div>
                <div>Points: <b>{fmtInt(csvState.points_done)}</b></div>
                <div>Folios: <b>{fmtInt(csvState.transactions_done)}</b></div>
                <div>Items: <b>{fmtInt(csvState.items_done)}</b></div>
              </div>
              {csvState.final_counts && (
                <div className="text-[12px] text-emerald-800 mt-2">
                  Final: {Object.entries(csvState.final_counts).map(([k,v]) => `${k}=${fmtInt(v)}`).join(" · ")}
                </div>
              )}
              {csvState.last_error && <div className="text-[12px] text-red-700 mt-1" data-testid="csv-last-error">Error: {csvState.last_error}</div>}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div className="md:col-span-2">
              <label className="block text-[11px] uppercase tracking-widest text-neutral-500 mb-1.5">Zip artifact URL</label>
              <input
                type="text"
                placeholder="https://customer-assets-.../Orchid%20files"
                value={csvUrl}
                onChange={(e) => setCsvUrl(e.target.value)}
                className="w-full border border-black/20 px-3 py-2 text-sm font-mono"
                data-testid="csv-url-input"
              />
            </div>
            <button
              onClick={startCsvImport}
              disabled={csvState?.running}
              className="k-btn bg-purple-700 text-white uppercase tracking-widest text-[11px] font-semibold disabled:opacity-50"
              data-testid="csv-import-start-btn"
            >
              Wipe & Import CSV
            </button>
          </div>
        </div>
      </section>

      {/* Historical Recovery — Date-Window Backfill */}
      <section className="bg-white border-2 border-emerald-700/50" data-testid="date-window-section">
        <SectionHeading title="Historical Recovery · Date-Window Backfill" />
        <div className="p-5 space-y-4">
          <div className="text-[13px] text-neutral-700 leading-relaxed">
            Fundle&apos;s <code className="text-[12px] bg-black/5 px-1 py-0.5">skip</code>-pagination
            fails past ~1.4M rows, leaving pre-2020 history unfetched.
            This tool walks Fundle <b>month-by-month</b> using
            <code className="text-[12px] bg-black/5 px-1 py-0.5">start_date=YYYY-MM-01&end_date=YYYY-MM-lastday</code>
            — each window has ~15-30K rows, so skip stays small and deep-OFFSET never
            triggers. Fully idempotent: safe to re-run any time.
          </div>
          {dwState && dwState.running && (
            <div className="bg-emerald-50 border border-emerald-200 p-3 text-sm space-y-1" data-testid="dw-running-panel">
              <div className="flex items-center gap-2 text-emerald-800 font-medium">
                <RefreshCw className="w-4 h-4 animate-spin" />
                Backfill in progress · {dwState.months_done ?? 0} / {dwState.months_total ?? "?"} months
              </div>
              <div className="text-[12px] text-emerald-700">
                Current window: <b>{dwState.current_window || "starting…"}</b>
              </div>
              <div className="text-[12px] text-neutral-700 grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
                <div>Rows pulled: <b>{fmtInt(dwState.rows_pulled)}</b></div>
                <div>Folios upserted: <b>{fmtInt(dwState.rows_upserted_txn)}</b></div>
                <div>Guests upserted: <b>{fmtInt(dwState.rows_upserted_cust)}</b></div>
                <div>Errors: <b className={dwState.errors ? "text-red-700" : ""}>{fmtInt(dwState.errors)}</b></div>
              </div>
              {dwState.last_error && <div className="text-[12px] text-red-700 mt-1" data-testid="dw-last-error">Error: {dwState.last_error}</div>}
            </div>
          )}
          {dwState && !dwState.running && dwState.months_done > 0 && (
            <div className="bg-neutral-50 border border-neutral-200 p-3 text-sm" data-testid="dw-last-run-panel">
              <div className="font-medium">Last run: {dwState.months_done}/{dwState.months_total} months · {fmtInt(dwState.rows_pulled)} rows pulled · {fmtInt(dwState.rows_upserted_txn)} folios / {fmtInt(dwState.rows_upserted_cust)} guests upserted</div>
              <div className="text-[12px] text-neutral-500">Finished: {dwState.finished_at ? fmtDateTime(dwState.finished_at) : "—"} · Errors: {fmtInt(dwState.errors)}</div>
              {dwState.last_error && <div className="text-[12px] text-red-700 mt-1">Error: {dwState.last_error}</div>}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div>
              <label className="block text-[11px] uppercase tracking-widest text-neutral-500 mb-1.5">From date</label>
              <input
                type="date"
                value={dwFrom}
                onChange={(e) => setDwFrom(e.target.value)}
                className="w-full border border-black/20 px-3 py-2 text-sm font-mono"
                data-testid="dw-from-date"
              />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-widest text-neutral-500 mb-1.5">To date</label>
              <input
                type="date"
                value={dwTo}
                onChange={(e) => setDwTo(e.target.value)}
                className="w-full border border-black/20 px-3 py-2 text-sm font-mono"
                data-testid="dw-to-date"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={startDateWindowBackfill}
                disabled={dwState?.running}
                className="flex-1 k-btn bg-emerald-700 text-white uppercase tracking-widest text-[11px] font-semibold disabled:opacity-50"
                data-testid="dw-start-btn"
              >
                <Database className="inline w-4 h-4 mr-1" /> Start Historical Backfill
              </button>
              {dwState?.running && (
                <button
                  onClick={stopDateWindowBackfill}
                  className="k-btn border-2 border-red-700 text-red-700 uppercase tracking-widest text-[11px] font-semibold"
                  data-testid="dw-stop-btn"
                >
                  Stop
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Config card */}
      <section className="bg-white border border-black/10">
        <SectionHeading title="Connector Configuration" />
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-[11px] uppercase tracking-widest text-neutral-500 mb-1.5">Base URL</label>
            <input
              type="text"
              defaultValue={cfg.base_url}
              onChange={(e) => setEdit({ ...edit, base_url: e.target.value })}
              className="w-full border border-black/20 px-3 py-2 text-sm font-mono"
              data-testid="bridge-cfg-base-url"
            />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-widest text-neutral-500 mb-1.5">Brand ID</label>
            <input
              type="number"
              defaultValue={cfg.brand_id}
              onChange={(e) => setEdit({ ...edit, brand_id: e.target.value })}
              className="w-full border border-black/20 px-3 py-2 text-sm font-mono"
              data-testid="bridge-cfg-brand-id"
            />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-widest text-neutral-500 mb-1.5">
              <KeyRound className="inline w-3 h-3 mr-1" /> API Key {cfg.api_key_masked && <span className="text-neutral-400 normal-case tracking-normal">· current: {cfg.api_key_masked}</span>}
            </label>
            <div className="relative">
              <input
                type={showKey ? "text" : "password"}
                placeholder="Leave blank to keep existing"
                onChange={(e) => setEdit({ ...edit, api_key: e.target.value })}
                className="w-full border border-black/20 px-3 py-2 pr-10 text-sm font-mono"
                data-testid="bridge-cfg-api-key"
              />
              <button onClick={() => setShowKey(!showKey)} className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-500" type="button" data-testid="bridge-cfg-show-key">
                {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-widest text-neutral-500 mb-1.5">Auth header</label>
            <select
              defaultValue={cfg.auth_header}
              onChange={(e) => setEdit({ ...edit, auth_header: e.target.value })}
              className="w-full border border-black/20 px-3 py-2 text-sm"
              data-testid="bridge-cfg-auth-header"
            >
              <option value="x-api-key">x-api-key</option>
              <option value="Authorization">Authorization (Bearer)</option>
            </select>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-widest text-neutral-500 mb-1.5">Sync interval (minutes)</label>
            <input
              type="number"
              min="1"
              defaultValue={cfg.sync_interval_min}
              onChange={(e) => setEdit({ ...edit, sync_interval_min: e.target.value })}
              className="w-full border border-black/20 px-3 py-2 text-sm font-mono"
              data-testid="bridge-cfg-interval"
            />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-widest text-neutral-500 mb-1.5">Page size (rows per request)</label>
            <input
              type="number"
              min="100"
              max="5000"
              defaultValue={cfg.page_size}
              onChange={(e) => setEdit({ ...edit, page_size: e.target.value })}
              className="w-full border border-black/20 px-3 py-2 text-sm font-mono"
              data-testid="bridge-cfg-page-size"
            />
          </div>
        </div>
        <div className="px-5 pb-5">
          <button
            onClick={saveConfig}
            disabled={savingCfg || Object.keys(edit).length === 0}
            className="k-btn kazo-bg-black text-white uppercase tracking-widest text-[11px] font-semibold disabled:opacity-50"
            data-testid="bridge-cfg-save-btn"
          >
            {savingCfg ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Save changes
          </button>
          <span className="ml-3 text-[11px] text-neutral-500">Interval change requires a backend restart to take effect.</span>
        </div>
      </section>

      {/* Help note */}
      <div className="bg-amber-50 border border-amber-200 px-5 py-4 text-[13px] text-amber-900">
        <strong>Points are pre-computed at Fundle.</strong> This bridge mirrors them as-is —
        Orchid Petals reports and AI analyses use the upstream values verbatim. The local earn / burn
        engine is disabled for bridge-sourced folios. To recompute, re-export from Fundle.
      </div>
    </div>
  );
}
